import React, { useEffect } from 'react'
import { Authenticator, View, Button, useAuthenticator } from '@aws-amplify/ui-react'
import CustomHeader from '../../../../components/CustomHeader'
import PAGE_TITLES from '../../../../consts/titles'
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import classRankedLogoMob from '../../../../assets/classrankedLogo_Mob.svg'
import classRankedLogo from '../../../../assets/classRankedLogo.svg'
import ROUTES from '../../../../consts/paths'
import { useNavigate } from 'react-router-dom'
import { grey } from '../../../../colors'

const formFields = {
  signIn: {
    username: {
      label: 'Institution email',
      placeholder: 'name@university.edu'
    },
    password: {
      label: 'Password',
      placeholder: 'Enter your password'
    }
  },
  resetPassword: {
    username: {
      label: 'Institution email',
      placeholder: 'name@university.edu'
    }
  }
}

interface ManualCognitoAuthenticatorProps {
  email: string
  onBack: () => void
}

// Renders on every Cognito route (via the Authenticator-level Header slot) — logo only, no route-specific copy.
const CognitoLogo: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box
      component='img'
      aria-label='ClassRanked logo'
      alt='ClassRanked logo'
      src={isMobile ? classRankedLogoMob : classRankedLogo}
      sx={{
        width: isMobile ? 64 : 240,
        height: 'auto',
        display: 'block',
        mb: 5
      }}
    />
  )
}

// Scoped to SignIn.Header so this copy only shows on the password step, not every Cognito route.
const SignInHeader: React.FC<{ email: string }> = ({ email }) => {
  React.useEffect(() => {
    if (!email) return
    setTimeout(() => {
      const usernameField = document.getElementsByName('username')?.[0] as HTMLInputElement | undefined
      if (usernameField) {
        usernameField.value = email
        usernameField.readOnly = true
      }
      const passwordField = document.getElementsByName('password')?.[0] as HTMLInputElement | undefined
      if (passwordField) passwordField.focus()
    }, 0)
  }, [email])

  return (
    <Stack spacing={1.5} alignItems='flex-start' sx={{ mb: 5 }}>
      <Typography
        component='h1'
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.75rem' },
          fontWeight: 600,
          lineHeight: 1.25,
          color: grey[250],
          letterSpacing: '-0.01em'
        }}
      >
        Sign in
      </Typography>
      <Typography
        sx={{
          fontSize: '0.9375rem',
          fontWeight: 400,
          lineHeight: 1.5,
          color: grey[700]
        }}
      >
        Enter your password to continue
      </Typography>
    </Stack>
  )
}

const SignInFooter: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { toForgotPassword } = useAuthenticator((context) => [context.toForgotPassword])

  return (
    <View className='auth-footer' data-amplify-footer=''>
      <Stack spacing={0.5} alignItems='flex-start'>
        <Button
          type='button'
          variation='link'
          size='small'
          onClick={toForgotPassword}
        >
          Forgot your password?
        </Button>
        <Button
          type='button'
          variation='link'
          size='small'
          onClick={onBack}
        >
          Back
        </Button>
      </Stack>
    </View>
  )
}

// Authenticator-level Footer slot — renders on every Cognito route except signIn, where
// SignInFooter already groups Back with Forgot password.
const CognitoFooter: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { route } = useAuthenticator((context) => [context.route])

  if (route === 'signIn') return null

  return (
    <View className='auth-footer' data-amplify-footer=''>
      <Button
        type='button'
        variation='link'
        size='small'
        onClick={onBack}
      >
        Back
      </Button>
    </View>
  )
}

export const CognitoAuthenticator: React.FC<ManualCognitoAuthenticatorProps> = ({ email, onBack }) => {
  const navigate = useNavigate()
  const { route } = useAuthenticator((ctx) => [ctx.route])

  useEffect(() => {
    if (route === 'authenticated') {
      navigate(ROUTES.HOME)
    }
  }, [route])

  return (
    <View className='auth-wrapper'>
      <CustomHeader title={PAGE_TITLES.LOGIN} />
      <Authenticator
        formFields={formFields}
        hideSignUp={true}
        components={{
          Header: () => <CognitoLogo />,
          Footer: () => <CognitoFooter onBack={onBack} />,
          SignIn: {
            Header: () => <SignInHeader email={email} />,
            Footer: () => <SignInFooter onBack={onBack} />
          }
        }}
      />
    </View>
  )
}
